import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SignInForm from '@/components/auth/SignInForm';
import AuthLayout from '@/components/auth/AuthLayout';
import { useAuth } from '@/contexts/auth';
import { useSignin } from '@/hooks/auth/use-signin';

const AuthPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { isAuthenticated, isLoading } = useAuth();
  const { signIn } = useSignin();
  
  // Get email from location state if redirected
  const email = location.state?.email || '';
  const showAccountExistsMessage = location.state?.showAccountExistsMessage || false;
  

  // Redirect authenticated users to home
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      console.log('[AuthPage] User is already authenticated, redirecting to home');
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);
  

  // Handle form submission directly with Supabase
  const handleFormSubmit = async (email: string, password: string) => {
    try {
      setIsSubmitting(true);
      await signIn(email, password);
    } catch (error) {
      console.error('Authentication error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  
  // Custom props for sign in form
  const signInProps = {
    defaultEmail: email,
    isShowingAccountExistsMessage: showAccountExistsMessage,
    onFormSubmit: async (email: string, password: string) => await handleFormSubmit(email, password)
  };
  
  // Title and description for sign in
  const pageContent = {
    title: "Sign In to Admin",
    description: "Welcome back to your admin portal"
  };
  
  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        <p className="text-sm text-muted-foreground">
          Checking authentication...
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <AuthLayout
        title={pageContent.title}
        description={pageContent.description}
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
        >
          <SignInForm {...signInProps} />
        </motion.div>
      </AuthLayout>
    </motion.div>
  );
};

export default AuthPage;
